import { useEffect, useState } from "react";
import { Header } from "@/components/brand/Header";
import { Footer } from "@/components/brand/Footer";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { LotCard } from "@/components/brand/LotCard";
import { StatusBadge } from "@/components/brand/StatusBadge";
import api from "@/lib/api";
import { useLocale } from "@/contexts/LocaleContext";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";
import { MarketTicker } from "@/components/brand/MarketTicker";
import { ExternalLink } from "lucide-react";

export default function BuyerDashboardPage() {
  const { user } = useAuth();
  const { currency, formatPrice, formatDate, t } = useLocale();
  const [lots, setLots] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [interests, setInterests] = useState([]);

  useEffect(() => {
    if (!user || user === false) return;
    api.get("/lots", { params: { currency } }).then((r) => setLots(r.data)).catch(() => {});
    api.get("/proposals").then((r) => setProposals(r.data)).catch(() => {});
    api.get("/interests").then((r) => setInterests(r.data)).catch(() => {});
  }, [user, currency]);

  if (user === false) return <Navigate to="/login" />;
  if (!user) return null;
  if (user.role === "buyer" && user.status !== "approved") return <Navigate to="/pending" />;
  if (user.role !== "buyer") return null;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <MarketTicker />
      <main className="flex-1 mx-auto max-w-[1600px] w-full px-4 sm:px-8 py-10">
        <div className="font-mono-data text-[10px] uppercase tracking-[0.3em] text-accent mb-3">
          {t("buyer.desk")} · {user.razao_social || user.name}
        </div>
        <h1 className="font-display text-4xl tracking-tighter font-black">{t("buyer.title")}</h1>
        <p className="text-muted-foreground mt-2">{t("buyer.subtitle")}</p>

        <Tabs defaultValue="market" className="mt-10">
          <TabsList className="rounded-none border border-border bg-card h-auto p-0 w-full justify-start">
            <TabsTrigger value="market" className="rounded-none px-6 py-3 font-mono-data text-xs uppercase tracking-wider data-[state=active]:bg-accent data-[state=active]:text-accent-foreground">
              {t("buyer.tab_market")} · {lots.length}
            </TabsTrigger>
            <TabsTrigger value="proposals" className="rounded-none px-6 py-3 font-mono-data text-xs uppercase tracking-wider data-[state=active]:bg-accent data-[state=active]:text-accent-foreground">
              {t("buyer.tab_proposals")} · {proposals.length}
            </TabsTrigger>
            <TabsTrigger value="interests" data-testid="buyer-tab-samples" className="rounded-none px-6 py-3 font-mono-data text-xs uppercase tracking-wider data-[state=active]:bg-accent data-[state=active]:text-accent-foreground">
              {t("buyer.tab_samples")} · {interests.length}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="market" className="mt-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {lots.map((l) => <LotCard key={l.id} lot={l} locked={false} />)}
            </div>
          </TabsContent>
          <TabsContent value="proposals" className="mt-6">
            <div className="border border-border bg-card overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-secondary/30 font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">
                    <th className="text-left p-3">{t("buyer.col_lot")}</th>
                    <th className="text-right p-3">{t("buyer.col_proposed_price")}</th>
                    <th className="text-right p-3">{t("buyer.col_bags")}</th>
                    <th className="text-left p-3">{t("common.status")}</th>
                    <th className="text-right p-3">{t("buyer.col_counter")}</th>
                    <th className="text-left p-3">{t("common.date")}</th>
                  </tr>
                </thead>
                <tbody>
                  {proposals.length === 0 ? (
                    <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">{t("buyer.no_proposals")}</td></tr>
                  ) : proposals.map((p) => (
                    <tr key={p.id} className="border-b border-border/60 hover:bg-secondary/30">
                      <td className="p-3">{p.lot_name}</td>
                      <td className="p-3 text-right font-mono-data">{formatPrice(p.proposed_price_per_bag, p.currency)}</td>
                      <td className="p-3 text-right font-mono-data">{p.bags_quantity}</td>
                      <td className="p-3"><StatusBadge status={p.status} /></td>
                      <td className="p-3 text-right font-mono-data">{p.counter_price ? formatPrice(p.counter_price, p.currency) : "—"}</td>
                      <td className="p-3 text-muted-foreground text-xs">{formatDate(p.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
          <TabsContent value="interests" className="mt-6">
            <div className="border border-border bg-card overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-secondary/30 font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">
                    <th className="text-left p-3">{t("buyer.col_lot")}</th>
                    <th className="text-right p-3">{t("buyer.col_bags")}</th>
                    <th className="text-left p-3">{t("samples.col_shipping")}</th>
                    <th className="text-left p-3">{t("samples.col_tracking")}</th>
                    <th className="text-left p-3">{t("common.status")}</th>
                    <th className="text-left p-3">{t("common.date")}</th>
                  </tr>
                </thead>
                <tbody>
                  {interests.length === 0 ? (
                    <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">{t("buyer.no_samples")}</td></tr>
                  ) : interests.map((i) => (
                    <tr key={i.id} data-testid={`buyer-sample-row-${i.id}`} className="border-b border-border/60 hover:bg-secondary/30 align-top">
                      <td className="p-3 text-xs font-medium">{i.lot_name || `#${i.lot_id?.slice(-6)}`}</td>
                      <td className="p-3 text-right font-mono-data">{i.bags_quantity}</td>
                      <td className="p-3 text-xs">
                        {i.shipping_address ? (
                          <div className="leading-snug">
                            <div className="font-medium">{i.shipping_recipient || i.shipping_address}</div>
                            <div className="text-muted-foreground">
                              {i.shipping_address}
                              {i.shipping_city ? `, ${i.shipping_city}` : ""}
                              {i.shipping_state ? ` · ${i.shipping_state}` : ""}
                            </div>
                            <div className="text-muted-foreground">{i.shipping_country} {i.shipping_postal_code ? `· ${i.shipping_postal_code}` : ""}</div>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">{t("samples.address_profile")}</span>
                        )}
                      </td>
                      <td className="p-3 text-xs font-mono-data">
                        {i.tracking_number ? (
                          i.tracking_url ? (
                            <a
                              href={i.tracking_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid={`buyer-sample-${i.id}-tracking-link`}
                              className="text-accent hover:underline inline-flex items-center gap-1"
                            >
                              <span>{i.tracking_number}</span>
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          ) : (
                            <span data-testid={`buyer-sample-${i.id}-tracking`}>{i.tracking_number}</span>
                          )
                        ) : (
                          <span className="text-muted-foreground/60">—</span>
                        )}
                        {i.tracking_carrier && (
                          <div className="text-muted-foreground/70 mt-0.5">{i.tracking_carrier}</div>
                        )}
                      </td>
                      <td className="p-3"><StatusBadge status={i.status} /></td>
                      <td className="p-3 text-muted-foreground text-xs">{formatDate(i.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </main>
      <Footer />
    </div>
  );
}
